import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import sitesHelper from "../../../../API/Sites/sitesHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import SiteSettingsView from "../SubComponents/SiteSettingsView";

let prestring = "toothfairyai-verification=";

function generateRandomString(length) {
  let characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return prestring + result;
}

const SitesSettings = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onFunctionCreated,
      selectedId,
      setShowSiteModal,
      requestValidation,
      displayTokenSection,
      setDisplayTokenSection,
      setInitialMapValues,
      onSiteDeleted = () => {},
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace, getPlanConfig } = useWorkspaces();

    const validationSchema = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      description: AppForms.yup.string().nullable().label().max(255),
      name: AppForms.yup.string().nullable().label().max(255),
      freeRoaming: AppForms.yup.boolean().nullable().label(),
      useProxy: AppForms.yup.boolean().nullable().label(),
      extractImages: AppForms.yup.boolean().nullable().label(),
      imgRetrievalPrompt: AppForms.yup.string().nullable().label().max(255),
      scrapingCycle: AppForms.yup.number().nullable().label(),
      url: AppForms.yup
        .string()
        .required()
        .label()
        .test(
          "is-domain",
          "Must be a valid url (e.g. https://mywebsite.com)",
          (value) => {
            const regex =
              /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9](?:\/[\w-]+)*\/?$/i;
            return regex.test(value);
          }
        )
        .max(255)
        .nullable(),
      customSitemap: AppForms.yup
        .string()
        .nullable()
        .label()
        .test(
          "is-sitemap-url",
          "Must be a valid sitemap URL (e.g. https://mywebsite.com/sitemap.xml)",
          (value) => {
            if (!value) return true;
            const regex =
              /^https?:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9](?:\/[\w\-\.~%]+)*(?:\.xml)?$/i;
            return regex.test(value);
          }
        )
        .max(255)
        .nullable(),
      allowedTopics: AppForms.yup.array().label().max(10).nullable(), // allowedTopics can be an empty array
      // add promptLength which must be a number between 128 to 512
    });

    const handleDeletion = async (id) => {
      try {
        const result = await sitesHelper.deleteSite(id);
        dispatch("SET_SITES", [...state.sites?.filter((u) => u?.id !== id)]);
        AppToasty.show(t("toasts.siteDeleted"), {
          type: "success",
        });

        setShowSiteModal?.(false);
        onSiteDeleted();
      } catch (error) {
        console.error("siteDeleteErrorCaption", error);
        AppToasty.show(t("toasts.siteDeletionFailed"), {
          type: "danger",
        });
      }
    };
    const identifyOptByID = (id) => {
      return state.sites.find((u) => u?.id === id);
    };

    const handleSiteItemSave = async (submitted) => {
      let v = { ...submitted };
      if (!v?.topics || v.topics?.length === 0)
        return AppToasty.show(t("toasts.selectAtLeastOneTopic"), {
          type: "danger",
        });
      if (typeof v?.topics === "object")
        v.topics = JSON.stringify(v.topics?.map((t) => t?.id));
      let newSites = [...state.sites];
      const _randomString = generateRandomString(40);
      try {
        let result;
        if (isEdit) {
          result = await sitesHelper.updateSite(selectedId, [
            {
              property: "name",
              value: v.url,
            },
            {
              property: "description",
              value: v.description,
            },
            {
              property: "freeRoaming",
              value: v.freeRoaming,
            },
            {
              property: "useProxy",
              value: v.useProxy,
            },
            {
              property: "extractImages",
              value: v?.extractImages,
            },
            {
              property: "imgRetrievalPrompt",
              value: v.imgRetrievalPrompt,
            },
            {
              property: "customSitemap",
              value: v.customSitemap,
            },
            {
              property: "scrapingCycle",
              // convert string to number
              value: Number(v?.scrapingCycle) || 0,
            },
            {
              property: "topics",
              value: v.topics,
            },
          ]);
          result = result?.data?.updateSite;
          const _updatedObject = identifyOptByID(result?.id);
          const updateIndex = newSites.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newSites[updateIndex] = result;
        } else {
          let allowedPaths = null;
          let refinedUrl = `${v.url}`;
          const purifiedUrl = v.url.replace("https://", "");
          // split the url taking the path after the / and use it for the allowed_paths
          if (purifiedUrl.includes("/")) {
            refinedUrl = `https://${purifiedUrl.split("/")[0]}`;
            allowedPaths = [v.url];
          }
          if (getPlanConfig()?.maxSites <= newSites.length)
            return AppToasty.show(
              `Your subscription plan does not allow you to add more than ${
                getPlanConfig()?.maxSites
              } sites`,
              {
                type: "danger",
              }
            );
          result = await sitesHelper.createSite({
            ...v,
            url: refinedUrl,
            allowedPaths,
            validationToken: _randomString,
            customSitemap: v?.customSitemap,
            status: "active",
            skipOwnerValidation: true,
            name: v.url,
            scrapingCycle: Number(v?.scrapingCycle) || 0,
            topics: v.topics,
            freeRoaming: v.freeRoaming,
            useProxy: false,
            extractImages: v?.extractImages,
            imgRetrievalPrompt: v.imgRetrievalPrompt,
            workspaceID: getActiveWorkspace()?.id,
          });
          result = result?.data?.createSite;
          newSites.push(result);
        }

        dispatch("SET_SITES", [...newSites]);
        if (!isEdit) {
          setInitialMapValues && setInitialMapValues({ ...result });
        }
        setShowSiteModal?.(false);
        backTo && navigateBack();
        const savedId = result?.id;
        onFunctionCreated && onFunctionCreated("success", savedId, submitted);
      } catch (error) {
        console.error(error);
        onFunctionCreated && onFunctionCreated("error", null, submitted);
      } finally {
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                  // height: 500,
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Site : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleSiteItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <SiteSettingsView
              setDisplayTokenSection={setDisplayTokenSection}
              displayTokenSection={displayTokenSection}
              requestValidation={requestValidation}
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        JSON.stringify(initialMapValues),
        JSON.stringify(state.sites),
        isEdit,
        displayTokenSection,
        AppToasty,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ]
    );
    return _view;
  }
);

export default SitesSettings;
